// Local Zulip-shaped types for the chat module. Kept separate from the
// `API.*` namespace in services/app/typings.d.ts — those describe our own
// backend's admin DTOs (API.ZulipChannel etc.), a different data source than
// these, which are proxied live from Zulip's own REST API.

// Above .chat-widget-container's z-index (1049, Chat.less) so any Modal
// opened from the chat panel (widget mode especially) always renders on top
// of it instead of behind it — antd's own Modal default (~1000) sits below.
export const CHAT_MODAL_Z_INDEX = 1060;

export type ZulipCredentials = {
  apiKey: string;
  email: string;
  baseUrl: string;
  userId: number;
};

export type ZulipStream = {
  stream_id: number;
  name: string;
  description?: string;
  invite_only?: boolean;
  is_archived?: boolean;
  date_created?: number;
  folder_id?: number | null;
  pin_to_top?: boolean;
  is_muted?: boolean;
};

// A realm channel folder (Zulip's own left-sidebar grouping mechanism) —
// see channelGrouping.ts for how subscriptions get bucketed by these.
export type ZulipChannelFolder = {
  id: number;
  name: string;
  description?: string;
  order: number;
  is_archived?: boolean;
};

export type PresenceStatus = "active" | "idle" | "offline";

export type ZulipTopic = {
  name: string;
  max_id: number;
};

export type ZulipUser = {
  user_id: number;
  email: string;
  full_name: string;
  avatar_url?: string | null;
  is_bot?: boolean;
  is_active?: boolean;
};

export type ZulipMessageRecipient = {
  id: number;
  email: string;
  full_name: string;
};

export type ZulipReactionType = "unicode_emoji" | "realm_emoji" | "zulip_extra_emoji";

// One user's reaction on a message — Zulip returns these as a flat sibling
// list on the message (`message.reactions`), one entry per (user, emoji)
// pair, not pre-grouped by emoji (see MessageBubble's grouping for display).
export type ZulipReaction = {
  emoji_name: string;
  emoji_code: string;
  reaction_type: ZulipReactionType;
  user_id: number;
};

export type ZulipMessage = {
  id: number;
  sender_id: number;
  sender_full_name: string;
  avatar_url?: string | null;
  timestamp: number;
  content: string;
  type: "stream" | "private";
  stream_id?: number; // present on stream-type messages only
  subject?: string; // topic name, for stream messages
  display_recipient: string | ZulipMessageRecipient[];
  flags?: string[];
  reactions?: ZulipReaction[];
};

/**
 * A single event from Zulip's long-poll `GET events` — see useZulipEvents.ts.
 * Deliberately a flat shape with optional per-type fields rather than a
 * discriminated union: a catch-all member wide enough for unhandled event
 * types (heartbeat, realm_user, etc.) defeats TS's discriminant narrowing
 * once mixed into a union with literal `type` members.
 */
export type ZulipEvent = {
  id: number;
  type: string;
  message?: ZulipMessage; // present when type === "message"
  // Present when type === "message" — Zulip sends this recipient's flags
  // (mentioned, read, etc.) as a sibling of `message`, not nested inside it.
  flags?: string[];
  op?: string; // present when type === "update_message_flags" | "subscription" | "typing" | "reaction" ("add" | "remove")
  flag?: string; // present when type === "update_message_flags"
  messages?: number[]; // present when type === "update_message_flags"
  message_id?: number; // present when type === "update_message" | "reaction"
  message_ids?: number[];
  rendered_content?: string;
  // present when type === "reaction" — see notify_reaction_update in
  // Zulip's zerver/actions/reactions.py. user_id is who added/removed it,
  // not necessarily the caller — this event is delivered to every recipient
  // of the message, including the actor themselves.
  user_id?: number;
  emoji_name?: string;
  emoji_code?: string;
  reaction_type?: string;
  // present when type === "typing" — see do_send_typing_notification /
  // do_send_stream_typing_notification in Zulip's zerver/actions/typing.py
  message_type?: "direct" | "stream";
  sender?: { user_id: number; email: string };
  recipients?: Array<{ user_id: number; email: string }>; // "direct" typing — the conversation's other participants
  stream_id?: number; // "stream" typing
  topic?: string; // "stream" typing
};

// A selected conversation — either a channel+topic thread, or a DM thread
// identified by the full set of participant user ids (including self).
export type Selection =
  | { kind: "channel"; streamId: number; streamName: string; topic: string }
  | { kind: "dm"; userIds: number[]; label: string };

export type DmConversation = {
  key: string; // sorted userIds joined by ','
  userIds: number[];
  label: string;
  lastMessage?: ZulipMessage;
  lastTimestamp: number;
};

// Where a "Forward message" (see ForwardMessageModal) resends a message to —
// mirrors Selection's two kinds, but a channel destination carries the free-typed
// topic name rather than one already-selected topic, since forwarding often
// targets a topic that doesn't exist yet.
export type ForwardDestination =
  | { kind: "channel"; stream: ZulipStream; topic: string }
  | { kind: "dm"; userIds: number[]; label: string };

export type ChatMode = "widget" | "sidebar";

// A Zulip-style left-sidebar "view" — a cross-conversation feed rather than
// a single channel/topic or DM thread. "inbox" is derived entirely from
// already-loaded unread state; the other four are fetched on demand via a
// `narrow` (see chatApi.getMessages) since they need actual message content.
export type ChatViewKind = "inbox" | "mentions" | "combined" | "starred" | "recent";
